import { CreateDateParams, CreateMonthParams, CreateYearParams } from '../model/types';

export const createDate = (params?: CreateDateParams) => {
  /**
   * берем язык из парамс, если в парамс яз не приходит то указываем default
   */
  const locale = params?.locale ?? 'default';
  /**
   * берем дату из парамс если дата не приходит то утсанавливаем текущую
   */
  const d = params?.date ?? new Date();
  const dayNumber = d.getDate();
  const day = d.toLocaleDateString(locale, { weekday: 'long' });
  const dayNumberInWeek = d.getDay() + 1;
  const dayShort = d.toLocaleDateString(locale, { weekday: 'short' });
  const year = d.getFullYear();
  const yearShort = d.toLocaleDateString(locale, { year: '2-digit' });
  const month = d.toLocaleDateString(locale, { month: 'long' });
  const monthShort = d.toLocaleDateString(locale, { month: 'short' });
  /**
   * прибавляем 1 т.к. getMonth() возвращ индекс месяца
   */
  const monthNumber = d.getMonth() + 1;
  const monthIndex = d.getMonth();
  const timestamp = d.getTime();
  const week = getWeekNumber(d);
  return {
    date: d,
    day,
    dayNumber,
    dayNumberInWeek,
    dayShort,
    year,
    yearShort,
    month,
    monthShort,
    monthNumber,
    monthIndex,
    timestamp,
    week,
  };
};

export const getWeekNumber = (date: Date) => {
  /**
   * date.getFullYear() - чтобы знать в каком году мы находимся, 0 - 1 месяц, 1 первое число месяца
   */
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  /**
   *определяем сколько дней прошло
   */
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

export const createMonth = (params: CreateMonthParams) => {
  const date = params?.date ?? new Date();
  const locale = params?.locale ?? 'default';

  const d = createDate({ date, locale });
  const { month: monthName, year, monthNumber, monthIndex } = d;

  /**
   * ф-ция к-ая создает нужный нам день
   */
  const getDay = (dayNumber: number) => createDate({ date: new Date(year, monthIndex, dayNumber), locale });
  const createMonthDays = () => {
    const days = [];

    for (let i = 0; i <= getMonthNumberOfDays(monthIndex, year) - 1; i += 1) {
      days[i] = getDay(i + 1);
    }
    return days;
  };
  return {
    getDay,
    monthName,
    monthIndex,
    monthNumber,
    year,
    createMonthDays,
  };
};

/**
 * ф-ция к-ая возвращает количество дней в месяце
 */
export const getMonthNumberOfDays = (monthIndex: number, yearNumber: number = new Date().getFullYear()) =>
  /**
   * возвращает нам последнее число месяца
   */
  new Date(yearNumber, monthIndex + 1, 0).getDate();

export const createYear = (params: CreateYearParams) => {
  const locale = params?.locale ?? 'default';

  const mouthCount = 12;
  const today = createDate();
  const year = params?.year ?? today.year;
  const monthNumber = params?.monthNumber ?? today.monthNumber;
  /**
   * вычитаем из monthNumber 1, так как new Date 2 параметром принимает индекс
   */
  const month = createMonth({ date: new Date(year, monthNumber - 1), locale });
  const getMonthDays = (monthIndex: number) =>
    createMonth({ date: new Date(year, monthIndex), locale }).createMonthDays();

  const createYearMonthes = () => {
    const monthes = [];
    for (let i = 0; i <= mouthCount - 1; i += 1) {
      monthes[i] = getMonthDays(i);
    }
    return monthes;
  };

  return {
    createYearMonthes,
    month,
    year,
  };
};

export const getMonthesNames = (locale: string = 'default') => {
  const monthesNames: {
    /**
     * ReturnType<typeof createDate> позволяет TypeScript автоматически определить типы этих свойств на основе возвращаемого типа функции createDate.
     */
    month: ReturnType<typeof createDate>['month'];
    monthShort: ReturnType<typeof createDate>['monthShort'];
    monthIndex: ReturnType<typeof createDate>['monthIndex'];
    date: ReturnType<typeof createDate>['date'];
    /**
     * создаем массив из 12 элементов, каждый элемент массива будет объектом, соответствующим описанию выше и массив будет использоваться для хранения информации о месяцах года.
     */
  }[] = Array.from({ length: 12 });
  const d = new Date();

  monthesNames.forEach((_, i) => {
    const { month, monthIndex, monthShort, date } = createDate({
      locale,
      date: new Date(d.getFullYear(), d.getMonth() + i, 1),
    });
    /**
     * заполняем объект след образом для каждого элемента массива
     */
    monthesNames[monthIndex] = { month, monthIndex, monthShort, date };
  });

  return monthesNames;
};

export const getWeekDaysNames = (firstWeekDay: number, locale = 'default') => {
  const weekDaysNames: {
    day: ReturnType<typeof createDate>['day'];
    dayShort: ReturnType<typeof createDate>['dayShort'];
  }[] = Array.from({ length: 7 });

  const d = new Date();
  weekDaysNames.forEach((_, i) => {
    const { day, dayNumberInWeek, dayShort } = createDate({
      locale,
      date: new Date(d.getFullYear(), d.getMonth(), d.getDay() + i),
    });
    weekDaysNames[dayNumberInWeek - 1] = { day, dayShort };
  });

  /**
   * вырезаем дни недели таким образом чтобы первым шел понедельник
   */
  return [...weekDaysNames.slice(firstWeekDay - 1), ...weekDaysNames.slice(0, firstWeekDay - 1)];
};

export const getYearsInterval = (year: number) => {
  const startYear = Math.floor(year / 10) * 10;
  return [...Array(10)].map((_, i) => startYear + i);
};

export const formatDate = (date: Date, format: string) => {
  const d = createDate({ date });

  return format
    .replace(/\bYYYY\b/, d.year.toString())
    .replace(/\bYYY\b/, d.yearShort)
    .replace(/\bWW\b/, d.week.toString().padStart(2, '0'))
    .replace(/\bW\b/, d.week.toString())
    .replace(/\bDDDD\b/, d.day)
    .replace(/\bDDD\b/, d.dayShort)
    .replace(/\bDD\b/, d.dayNumber.toString().padStart(2, '0'))
    .replace(/\bD\b/, d.dayNumber.toString())
    .replace(/\bMMMM\b/, d.month)
    .replace(/\bMMM\b/, d.monthShort)
    .replace(/\bMM\b/, d.monthNumber.toString().padStart(2, '0'))
    .replace(/\bM\b/, d.monthNumber.toString());
};

export const checkDateIsEqual = (date1: Date, date2: Date) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

export const checkIsToday = (date: Date) => {
  const today = new Date();

  return checkDateIsEqual(today, date);
};

/**
 * возвращает даты в диапазоне между первой и последней датой
 */
export const getDatesInRange = (start: Date, end: Date): Date[] => {
  const dates: Date[] = [];
  const currentDate = new Date(start);

  while (currentDate <= end) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};
